import React, { useState } from 'react'
import { Button, Flex, Popover, Segmented, Typography, theme } from 'antd'
import Icon, { CloseOutlined, DownOutlined } from '@ant-design/icons'
import { SlidersHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { DisplayScale, getScaleSuffix } from '../../utils/numberScale'
import { formatVariableValue } from '../../../pages/reporting/custom/components/grid/utils/valueFormatting'
import { VariableType } from '../../../components/Table/types'

export interface NumberFormatControlsProps {
  displayScale: DisplayScale
  onDisplayScaleChange: (scale: DisplayScale) => void
  decimals: number
  onDecimalsChange: (decimals: number) => void
  disabled?: boolean
}

const EXAMPLE_VALUE = 1234567
const DECIMAL_OPTIONS = [0, 1, 2]

/**
 * Toolbar button opening a popover for picking a value's display scale
 * (default/thousands/millions) and decimal count. Shared by
 * IncomeStatementTable and BalanceSheetTable so both grids stay in sync.
 */
export const NumberFormatControls: React.FC<NumberFormatControlsProps> = ({
  displayScale,
  onDisplayScaleChange,
  decimals,
  onDecimalsChange,
  disabled = false
}) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const [open, setOpen] = useState(false)

  const scaleLabels: Record<DisplayScale, string> = {
    [DisplayScale.default]: t('numberFormat:noScale'),
    [DisplayScale.thousands]: t('displayScale:thousands'),
    [DisplayScale.millions]: t('displayScale:millions'),
    [DisplayScale.auto]: t('displayScale:auto')
  }

  const scaleOptions = [DisplayScale.default, DisplayScale.thousands, DisplayScale.millions].map(value => ({
    label: scaleLabels[value],
    value
  }))

  const exampleBefore = formatVariableValue(
    EXAMPLE_VALUE,
    { displayScale: DisplayScale.default, decimals: 0 },
    VariableType.absolute
  )
  const exampleAfter = formatVariableValue(EXAMPLE_VALUE, { displayScale, decimals }, VariableType.absolute)

  const title = (
    <Flex justify="space-between" align="start" gap={token.marginSM}>
      <Flex vertical>
        <Typography.Text strong>{t('numberFormat:title')}</Typography.Text>
        <Typography.Text type="secondary" style={{ fontWeight: 'normal' }}>
          {t('numberFormat:description')}
        </Typography.Text>
      </Flex>
      <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setOpen(false)} />
    </Flex>
  )

  const content = (
    <Flex vertical gap={token.margin} style={{ width: 300 }}>
      <Flex vertical gap={token.marginXS}>
        <Typography.Text>{t('displayScale:label')}</Typography.Text>
        <Segmented<DisplayScale>
          block
          className="segmented-no-animation"
          value={displayScale}
          onChange={onDisplayScaleChange}
          options={scaleOptions}
        />
      </Flex>
      <Flex vertical gap={token.marginXS}>
        <Typography.Text>{t('numberFormat:decimals')}</Typography.Text>
        <Segmented<number>
          block
          className="segmented-no-animation"
          value={decimals}
          onChange={onDecimalsChange}
          options={DECIMAL_OPTIONS.map(value => ({ label: String(value), value }))}
        />
      </Flex>
      <Flex
        justify="space-between"
        align="center"
        gap={token.marginSM}
        style={{
          background: token.colorFillQuaternary,
          borderRadius: token.borderRadius,
          padding: `${token.paddingXS}px ${token.paddingSM}px`
        }}
      >
        <Typography.Text type="secondary">{t('numberFormat:example')}</Typography.Text>
        <Typography.Text strong>
          {exampleBefore} → {exampleAfter}
        </Typography.Text>
      </Flex>
    </Flex>
  )

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      open={open && !disabled}
      onOpenChange={setOpen}
      title={title}
      content={content}
    >
      <Button disabled={disabled} icon={<Icon component={() => <SlidersHorizontal size={14} />} />}>
        {t('numberFormat:triggerLabel', {
          // Short scale suffix (k/M) matches the grid values; omitted when unscaled
          format: [getScaleSuffix(displayScale), t('numberFormat:decimalsShort', { decimals })]
            .filter(Boolean)
            .join(' · ')
        })}
        <DownOutlined />
      </Button>
    </Popover>
  )
}

export default NumberFormatControls
